import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './contexts/AuthContext';
import { NotificationProvider } from './contexts/NotificationContext';
import ProtectedRoute from './components/ProtectedRoute';
import Layout from './components/Layout';
import LoginPage from './pages/LoginPage';
import DashboardPage from './pages/DashboardPage';
import UserManagement from './pages/UserManagement';
import RoleManagement from './pages/RoleManagement';
import FinanceManagement from './pages/FinanceManagement';
import AssetEditRequests from './pages/AssetEditRequests';
import AssetManagement from './pages/AssetManagement';
import FinancialAnalytics from './pages/FinancialAnalytics';
import InventoryAnalytics from './pages/InventoryAnalytics';
import ManualSalesPerformanceAnalytics from './pages/ManualSalesPerformanceAnalytics';
import SmartRedirect from './components/SmartRedirect';
import NotificationSnackbar from './components/NotificationSnackbar';
import './App.css';

// Tema personalizado do Material-UI
let theme = createTheme({
  breakpoints: {
    values: { xs: 0, sm: 600, md: 900, lg: 1200, xl: 1536 }
  },
  palette: {
    primary: {
      main: '#1976d2',
      light: '#42a5f5',
      dark: '#1565c0',
    },
    secondary: {
      main: '#dc004e',
      light: '#ff5983',
      dark: '#9a0036',
    },
    background: {
      default: '#f5f5f5',
      paper: '#ffffff',
    },
  },
  typography: {
    fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
    fontSize: 14,
    body1: { fontSize: 14 },
    body2: { fontSize: 13 },
    h4: {
      fontWeight: 600,
      fontSize: '1.75rem'
    },
    h5: {
      fontWeight: 500,
      fontSize: '1.5rem'
    },
    h6: {
      fontWeight: 500,
      fontSize: '1.125rem'
    },
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiContainer: {
      defaultProps: { maxWidth: 'xl' }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: '0 2px 8px rgba(0,0,0,0.1)',
          '&:hover': {
            boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
          },
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('md')]: {
            overflowX: 'auto'
          }
        })
      }
    },
    MuiTableCell: {
      styleOverrides: {
        root: ({ theme }) => ({
          [theme.breakpoints.down('sm')]: { padding: '8px 12px' }
        })
      }
    },
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 500,
        },
      },
    },
    MuiChip: {
      styleOverrides: {
        root: {
          fontWeight: 500,
        },
      },
    },
  },
});
// Tornar tipografia responsiva por breakpoint
theme = responsiveFontSizes(theme);

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <NotificationProvider>
        <AuthProvider>
          <Router>
            <div className="App">
              <Routes>
                {/* Rota de login */}
                <Route path="/login" element={<LoginPage />} />
                
                {/* Rotas protegidas */}
                <Route path="/dashboard" element={
                  <ProtectedRoute requiredPermission="analytics.dashboard.read">
                    <Layout>
                      <DashboardPage />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Rotas administrativas */}
                <Route path="/admin/users" element={
                  <ProtectedRoute requiredPermission="users.read">
                    <Layout>
                      <UserManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/roles" element={
                  <ProtectedRoute requiredPermission="roles.read">
                    <Layout>
                      <RoleManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/finance/transactions" element={
                  <ProtectedRoute requiredPermissionsAny={["finance.transactions.read.all", "finance.transactions.read.self"]}>
                    <Layout>
                      <FinanceManagement />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/finance/asset-edit-requests" element={
                  <ProtectedRoute requiredPermission="finance.transactions.verify">
                    <Layout>
                      <AssetEditRequests />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/assets" element={
                  <ProtectedRoute requiredPermission="inventory.assets.read">
                    <Layout>
                      <AssetManagement />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Rotas de Analytics */}
                <Route path="/admin/finance/analytics" element={
                  <ProtectedRoute requiredPermission="analytics.finance.read">
                    <Layout>
                      <FinancialAnalytics />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                <Route path="/admin/inventory/analytics" element={
                  <ProtectedRoute requiredPermission="analytics.inventory.read">
                    <Layout>
                      <InventoryAnalytics />
                    </Layout>
                  </ProtectedRoute>
                } />

                <Route path="/admin/analytics/manual-sales-performance" element={
                  <ProtectedRoute requiredPermission="analytics.manual_sales_performance.read">
                    <Layout>
                      <ManualSalesPerformanceAnalytics />
                    </Layout>
                  </ProtectedRoute>
                } />
                
                {/* Rota padrão - redireciona baseado nas permissões */}
                <Route path="/" element={
                  <ProtectedRoute>
                    <SmartRedirect />
                  </ProtectedRoute>
                } />
                
                {/* Rota catch-all - redireciona baseado nas permissões */}
                <Route path="*" element={
                  <ProtectedRoute>
                    <SmartRedirect />
                  </ProtectedRoute>
                } />
              </Routes>
              
              {/* Componente de notificações globais */}
              <NotificationSnackbar />
            </div>
          </Router>
        </AuthProvider>
      </NotificationProvider>
    </ThemeProvider>
  );
}

export default App;
